import type { LabTestDashboardTest } from '~/types'

import { sortLabDashboardTests } from './sortLabDashboardTests'

/**
 * Reconciles incoming dashboard tests with the order already shown in the UI (e.g. list
 * row). Detail/list APIs often return the same tests in different array order; sorting by
 * orderDate/id does not match the list when those fields tie, so we keep prior order by id.
 */
export function mergeLabDashboardTestsPreservingOrder(
  previousTests: LabTestDashboardTest[] | undefined,
  incomingTests: LabTestDashboardTest[],
): LabTestDashboardTest[] {
  if (!previousTests?.length) {
    return sortLabDashboardTests(incomingTests)
  }

  const incomingById = new Map(incomingTests.map((test) => [test.id, test]))

  const { ordered, usedIds } = previousTests.reduce(
    (
      acc: {
        ordered: LabTestDashboardTest[]
        usedIds: Set<string>
      },
      prev,
    ) => {
      const next = incomingById.get(prev.id)
      if (next) {
        acc.ordered.push(next)
        acc.usedIds.add(next.id)
      }
      return acc
    },
    { ordered: [], usedIds: new Set<string>() },
  )

  return [...ordered, ...incomingTests.filter((test) => !usedIds.has(test.id))]
}
